// @flow
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useStores } from '../stores';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { Hidden, Box, Grid, Radio } from '@material-ui/core';
import { toJS } from 'mobx';
import InfoOutlined from '@material-ui/icons/InfoOutlined';

import {
  DialogHeader,
  Dialog,
  DialogFooter,
  DialogBody,
} from './common/Dialog';
import AddressList from './common/AddressList';
import LinkButton from './common/LinkButton';
import Button from './common/Button';
import appStyles from '../../../config/appStyles.js';
import homeIcon from '../../../assets/images/home.svg';
import mapPinIcon from '../../../assets/images/map-pin.svg';
import AddressProcessingBanner from './common/AddressProcessingBanner';
import { isCurrentAddress } from '../../../utils/addressMatch';
import PickupPointMap from './common/PickupPointMap';
import Progress from './common/Progress';
import usePickupPoints from './common/usePickupPoints';
import { getKeys } from '../utils/dictionary';
import {
  isPickupPointAddress,
  isPickupPointCountry,
  pickupPointToAddress,
} from '../../../utils/pickupPoint';

const useStyles = makeStyles((theme) => appStyles.changeAddressModal(theme));

const newAddrFormId = 'new-addr-form';
const pendingPickupId = 'pending-pickup';
const pendingAddrId = 'pending-address';
const mapBannerKey = 'ooh.modal.notification.text';

const ChangeAddressModal = observer(({ className, open, onClose, address }) => {
  const classes = useStyles();
  const addrList = useRef(null);
  const addrBeforeEdit = useRef();

  const { t } = useTranslation(['common', 'components']);

  const { addressStore, uiStore, userStore, orderStore } = useStores();
  const { addresses } = userStore;
  const { currentOrder } = orderStore;

  const savedAddresses = useMemo(
    () => (addresses || []).filter((a) => !isPickupPointAddress(a)),
    [addresses],
  );
  const pickupAddresses = useMemo(
    () => (addresses || []).filter(isPickupPointAddress),
    [addresses],
  );

  // the pickup point flow is offered only where the provider delivers
  const pickupEnabled = isPickupPointCountry(uiStore.country);

  // const [updatedAddresses, setUpdatedAddresses] = useState(toJS(addresses));
  const [selectedAddr, setSelectedAddr] = useState(false);
  const [actionEnabled, setActionEnabled] = useState(true);
  const [defaultAddr, setDefaultAddr] = useState();
  const [isNew, setIsNew] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [pendingPickupAddr, setPendingPickupAddr] = useState();
  const [pendingNewAddr, setPendingNewAddr] = useState();
  const [mapBanner, setMapBanner] = useState();

  const currentAddr = useMemo(() => {
    const list = addresses || [];
    if (!address) {
      return list.find((a) => a.defaultAddress);
    }
    // a pickup point copies street and postal code from the address it was created
    // from, so the formatted string cannot tell the two apart: the locker id can
    const lockerId = address.pudo && address.pudo.lockerId;
    if (lockerId) {
      return list.find((a) => a.pudo && a.pudo.lockerId === lockerId);
    }
    return list.find(
      (a) => !isPickupPointAddress(a) && isCurrentAddress(a, address.formatted),
    );
  }, [addresses, address]);
  const currentPickupAddr = isPickupPointAddress(currentAddr)
    ? currentAddr
    : undefined;
  const pickupAddr = pendingPickupAddr || currentPickupAddr;

  const standardList = useMemo(() => {
    const list = toJS(savedAddresses);
    if (pendingNewAddr) {
      list.push(pendingNewAddr);
    }
    return list;
  }, [savedAddresses, pendingNewAddr]);

  // saved points, deduped by locker id, plus the one just picked on the map
  const pickupList = useMemo(() => {
    const list = [];
    const seen = {};
    toJS(pickupAddresses).forEach((a) => {
      const lockerId = a.pudo && a.pudo.lockerId;
      if (lockerId && seen[lockerId]) {
        return;
      }
      seen[lockerId] = true;
      list.push(a);
    });
    if (pendingPickupAddr) {
      list.push({ ...pendingPickupAddr, id: pendingPickupId });
    }
    return list;
  }, [pickupAddresses, pendingPickupAddr]);

  // Which row each list highlights. Derived from the selection instead of relying
  // on id equality: sentinel ids and filtered lists make that ambiguous, and two
  // rows sharing an id would both light up.
  const sameEntry = (a, b) =>
    !!a &&
    !!b &&
    (a.pudo && a.pudo.lockerId
      ? a.pudo.lockerId === (b.pudo && b.pudo.lockerId)
      : a.rndId
      ? a.rndId === b.rndId
      : a.id === b.id);

  const pickupValue = isPickupPointAddress(selectedAddr)
    ? (pickupList.find((a) => sameEntry(a, selectedAddr)) || {}).id
    : undefined;
  const standardValue = isPickupPointAddress(selectedAddr)
    ? undefined
    : defaultAddr;

  const { addresses: pickupListView, loading: loadingPoints } =
    usePickupPoints(pickupList);

  // saved points stay selectable even where the flow is not offered any more:
  // only picking a new one on the map needs the country to be enabled
  const pickupVisible = pickupEnabled || pickupList.length > 0;

  useEffect(() => {
    setDefaultAddr(currentAddr ? currentAddr.id : undefined);
    // the pickup list highlights the selection, not the default radio, so the
    // current point has to start out selected or the modal opens on nothing
    setSelectedAddr(
      isPickupPointAddress(currentAddr) ? toJS(currentAddr) : false,
    );
  }, [open, currentAddr]);

  useEffect(() => {
    setActionEnabled(true);
    setIsNew(false);
    setShowMap(false);
    setPendingPickupAddr(undefined);
    setPendingNewAddr(undefined);
  }, [open]);

  useEffect(() => {
    if (!showMap || mapBanner !== undefined) return;
    getKeys({ text: mapBannerKey }).then(({ text }) =>
      setMapBanner(text && !text.startsWith('[Dictionary]') ? text : ''),
    );
  }, [showMap, mapBanner]);

  const _triggerAnalyticsEvent = (evtName) => {
    console.debug(
      '[TagManager] Add event in subscription addresses modal: ' + evtName,
    );
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'myArea',
      event_data: {
        category: 'myArea_subscription',
        action: 'click_button',
        label: evtName,
      },
    });
  };

  const handleSaved = (addr) => {
    // saving the address the order already ships to: the backend never changes it,
    // so there is nothing to wait for
    const changed = !currentAddr || addr.id !== currentAddr.id;
    if (changed) {
      orderStore.setPendingAddress(currentOrder, address);
    }
    onClose();
    uiStore.openModal(t('common:changesSaved'), '', {
      confirmLabel: t('components:Subscribe.successfullModal.closeWindow'),
      onConfirm: () => uiStore.closeModal(),
      children: changed ? <AddressProcessingBanner /> : null,
    });
  };

  const handleAddrSelect = (addr) => {
    setDefaultAddr(addr.id);
    const stored = pickupList.find((a) => a.id === addr.id);
    setSelectedAddr(stored || addr);
  };

  const openMap = () => {
    setPendingPickupAddr(undefined);
    setSelectedAddr(false);
    setShowMap(true);
  };

  const handlePickupSelect = (selection) => {
    setShowMap(false);
    const defaultAddress =
      savedAddresses.find((a) => a.defaultAddress) || savedAddresses[0] || {};
    const addr = pickupPointToAddress(selection, toJS(defaultAddress));
    const lockerId = addr.pudo && addr.pudo.lockerId;
    const stored =
      lockerId &&
      pickupAddresses.find((a) => a.pudo && a.pudo.lockerId === lockerId);
    if (stored) {
      setPendingPickupAddr(undefined);
      setSelectedAddr(toJS(stored));
      setDefaultAddr(stored.id);
      return;
    }

    setPendingPickupAddr(addr);
    setSelectedAddr(addr);
    setDefaultAddr(pendingPickupId);
  };

  const handleEdit = (edit, addr) => {
    setActionEnabled(!edit);
    if (edit) {
      addrBeforeEdit.current = defaultAddr;
      setDefaultAddr(addr.id);
      // setSelectedAddr(addr);
    }
  };

  const handleSubmit = (addr, newAddresses, cb) => {
    const addrToAdd = {
      ...addr,
      id: pendingAddrId,
      isNew: false,
      addressLine1: uiStore.siteId.endsWith('DE')
        ? addressStore.formatAddress({
            street: addr.addressLine1,
            street_number: addr.normStreetNum,
          })
        : addr.addressLine1,
    };

    setPendingNewAddr(addrToAdd);
    setSelectedAddr(addrToAdd);
    setDefaultAddr(pendingAddrId);
    setIsNew(false);
    if (cb) cb();
  };

  const handleAdd = () => {
    _triggerAnalyticsEvent('Add New Address');
    addrList.current.add();
    setIsNew(true);
  };

  const handleConfirm = () => {
    _triggerAnalyticsEvent('Add New Address_Save Changes');
    uiStore.setLoader(true);
    const finalAddress = { ...selectedAddr };
    finalAddress.normStreet = finalAddress.normStreet
      ? finalAddress.normStreet
      : selectedAddr.addressLine1;
    finalAddress.addressLine1 = uiStore.siteId.endsWith('DE')
      ? addressStore.formatAddress({
          street: selectedAddr.addressLine1,
          street_number: selectedAddr.normStreetNum,
        })
      : selectedAddr.addressLine1;
    orderStore
      .updateAddress(currentOrder, finalAddress)
      .then((res) => {
        if (res) {
          const done = () => {
            handleSaved(finalAddress);
            orderStore.fetchShipments();
          };

          const isNewInBook =
            finalAddress.id === pendingAddrId ||
            (isPickupPointAddress(finalAddress) && !finalAddress.rndId);
          if (isNewInBook) {
            const book = toJS(addresses || []);
            const toAdd = { ...finalAddress };
            toAdd.defaultAddress =
              !isPickupPointAddress(toAdd) &&
              !book.some((a) => a.defaultAddress);
            // the order already ships to the new address: a failed address book
            // save only leaves it out of the saved list
            userStore.updateAddress(book.concat([toAdd]), () => done());
          } else {
            done();
          }
        } else {
          uiStore.showGenericError();
        }
        uiStore.setLoader(false);
      })
      .catch(() => {
        uiStore.showGenericError();
        uiStore.setLoader(false);
      });
  };

  const pickupSelected = selectedAddr
    ? isPickupPointAddress(selectedAddr)
    : !!currentPickupAddr;

  const handleMapCancel = () => {
    setPendingPickupAddr(undefined);
    setSelectedAddr(false);
    setShowMap(false);
  };

  const handleCancel = () => {
    _triggerAnalyticsEvent('Add New Address_Cancel');
    onClose();
  };

  // the inline form's cancel goes back to the address list, not out of the modal
  const handleFormCancel = () => {
    setIsNew(false);
    setActionEnabled(true);
    setDefaultAddr(addrBeforeEdit.current);
  };

  return (
    <>
      <Dialog
        className={clsx(classes.modal, showMap && classes.mapModal, className)}
        classes={showMap ? { paper: classes.mapPaper } : undefined}
        open={open}
        id="change-address-modal"
        maxWidth={showMap ? 'lg' : 'md'}
        fullWidth={showMap}>
        <DialogHeader
          title={
            showMap
              ? t('components:ChangeAddressModal.pickupPointTitle')
              : t('components:SubscriptionDetails.title')
          }
          onClose={onClose}
        />
        {showMap && pickupEnabled ? (
          <DialogBody className={classes.mapBody}>
            {mapBanner && (
              <Box className={classes.mapBanner}>
                <InfoOutlined fontSize="large" />
                <span>{mapBanner}</span>
              </Box>
            )}
            <PickupPointMap onSelect={handlePickupSelect} />
          </DialogBody>
        ) : (
          <DialogBody className={classes.modalBody}>
            <Box pt={3}>
              <Box>
                <Box className={classes.sectionTitle}>
                  {t('components:Package.willBeShippedTo')}
                </Box>
                <Box className={classes.sectionHeader}>
                  <img className={classes.sectionIcon} src={homeIcon} alt="" />
                  <Box className={classes.sectionLabel}>
                    {t('components:ChangeAddressModal.savedAddresses')}
                  </Box>
                </Box>
                <Box className={classes.sectionHint}>
                  {t('components:ChangeAddressModal.savedAddressesHint')}
                </Box>
                <AddressList
                  key={pendingNewAddr ? 'with-pending' : 'saved'}
                  id={'changeAddressModal'}
                  ref={addrList}
                  formId={isNew ? newAddrFormId : undefined}
                  variant="outlined"
                  value={standardValue}
                  addresses={standardList}
                  onSelect={handleAddrSelect}
                  onDelete={() => {}}
                  onCancel={handleFormCancel}
                  onSubmit={handleSubmit}
                  onEdit={handleEdit}
                  actionsEnabled={actionEnabled}
                  hideActions
                  showOnlyNew={isNew}
                />
              </Box>
            </Box>
            {!isNew && (
              <button
                className={classes.addButton}
                type="button"
                onClick={handleAdd}>
                {t('components:AddressesList.newAddress')}
              </button>
            )}
            {!isNew && pickupVisible && (
              <Box className={classes.pickupSection}>
                <Box className={classes.sectionHeader}>
                  <img
                    className={classes.sectionIcon}
                    src={mapPinIcon}
                    alt=""
                  />
                  <Box className={classes.sectionLabel}>
                    {t('components:ChangeAddressModal.pickupPoint')}
                  </Box>
                </Box>
                <Box className={classes.sectionHint}>
                  {t('components:ChangeAddressModal.pickupPointHint')}
                </Box>
                {loadingPoints ? (
                  <Progress variant="medium" />
                ) : pickupList.length > 0 ? (
                  <>
                    <AddressList
                      id={'changeAddressModalPickup'}
                      variant="outlined"
                      value={pickupValue}
                      addresses={pickupListView}
                      onSelect={handleAddrSelect}
                      onDelete={() => {}}
                      onCancel={handleCancel}
                      onSubmit={() => {}}
                      onEdit={() => {}}
                      actionsEnabled={actionEnabled}
                      hideActions
                    />
                    {pickupEnabled && (
                      <button
                        className={classes.addButton}
                        type="button"
                        onClick={openMap}>
                        {t('components:ChangeAddressModal.pickupPointAdd')}
                      </button>
                    )}
                  </>
                ) : (
                  <Box
                    className={clsx(
                      classes.pickupOption,
                      pickupSelected && classes.pickupOptionSelected,
                    )}
                    onClick={openMap}>
                    <Radio
                      color="primary"
                      checked={!!pickupSelected}
                      onChange={openMap}
                    />
                    <Box className={classes.pickupOptionInfo}>
                      <Box className={classes.pickupOptionRow}>
                        <span className={classes.pickupOptionLabel}>
                          {t('components:ChangeAddressModal.pickupPointOption')}
                        </span>
                        {pickupAddr && (
                          <span className={classes.pickupOptionName}>
                            {pickupAddr.label}
                          </span>
                        )}
                      </Box>
                      {pickupAddr && (
                        <Box className={classes.pickupOptionRow}>
                          <span className={classes.pickupOptionLabel}>
                            {t('components:AddressesList.address')}:
                          </span>
                          <span className={classes.pickupOptionAddress}>
                            {[
                              pickupAddr.addressLine1,
                              pickupAddr.postalCode,
                              pickupAddr.city,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </span>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </DialogBody>
        )}
        <DialogFooter className={classes.modalFooter}>
          <Grid item xs={12} md={12} className={classes.gridButton}>
            <Box className={classes.buttonSectionFooter}>
              {showMap ? (
                <Button
                  className={classes.cancelButton}
                  onClick={handleMapCancel}
                  variant="outlined">
                  {t('common:cancel')}
                </Button>
              ) : (
                <>
                  <Button
                    className={classes.cancelButton}
                    disabled={!selectedAddr}
                    onClick={handleCancel}
                    variant="outlined">
                    {t('common:cancel')}
                  </Button>

                  <Button
                    className={classes.confirmButton}
                    type="submit"
                    form={isNew ? newAddrFormId : undefined}
                    size="large"
                    disabled={!selectedAddr}
                    onClick={isNew ? null : handleConfirm}>
                    {t('common:saveChanges')}
                  </Button>
                </>
              )}
            </Box>
          </Grid>
        </DialogFooter>
      </Dialog>
    </>
  );
});

export default ChangeAddressModal;
